import { TsosStateProps } from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { ApiCommonExternalDtoMeasurementLogExternalDto } from '@redux/apiSlices/measurementLogApi';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './../store';

type Option = {
  id: number;
  value?: string;
  label: string;
};

export type importedMeaserementOverviewParameterProps = {
  selectedTso: string;
  activeTsos: TsosStateProps[] | [];
  activeTsoName: string;
  start: string | undefined;
  end: string | undefined;
  deviceTechnicalNames: { [key: string]: string[] };
  measurementLogs: ApiCommonExternalDtoMeasurementLogExternalDto[] | undefined | null;
  dataToBeDisplayedInChart: enhancedMeasurementLog[];
  aggregationInterval: Option | undefined;
  aggregationIntervalType: string | undefined;
};

export type enhancedMeasurementLog = {
  id: number;
  tsoId: number;
  start: string | undefined;
  end: string | undefined;
  measurementDeviceId: number;
  aggregationIntervalId: number;
  deviceLabel: string;
  measurementPointLabel: string;
  substationLabel: string;
  // measurementLogs: ApiCommonExternalDtoMeasurementLogExternalDto[] | undefined | null;
};

const initialState: importedMeaserementOverviewParameterProps = {
  selectedTso: '',
  activeTsos: [],
  activeTsoName: '',
  start: undefined,
  end: undefined,
  deviceTechnicalNames: {},
  measurementLogs: [],
  dataToBeDisplayedInChart: [],
  aggregationInterval: undefined,
  aggregationIntervalType: undefined,
};
const importedMeasurementsOverviewSlice = createSlice({
  name: 'importedMeasurementsOverview',
  initialState,
  reducers: {
    setSelectedTso: (state, action: PayloadAction<string>) => {
      state.selectedTso = action.payload;
    },
    setActiveTsos: (state, action: PayloadAction<TsosStateProps[]>) => {
      state.activeTsos = action.payload;
    },
    setActiveTsoName: (state, action: PayloadAction<string>) => {
      state.activeTsoName = action.payload;
    },
    setStart: (state, action: PayloadAction<string>) => {
      state.start = action.payload;
    },
    setEnd: (state, action: PayloadAction<string>) => {
      state.end = action.payload;
    },
    setDeviceTechnicalNames: (state, action: PayloadAction<{ [key: string]: string[] }>) => {
      state.deviceTechnicalNames = action.payload;
    },
    setDataToBeDisplayedInChart: (state, action: PayloadAction<enhancedMeasurementLog[]>) => {
      state.dataToBeDisplayedInChart = action.payload;
    },
    setMeasurementLogs: (state, action: PayloadAction<any>) => {
      state.measurementLogs = action.payload;
    },
    setAggregationInterval: (state, action: PayloadAction<Option>) => {
      state.aggregationInterval = action.payload;
    },
    setAggregationIntervalType: (state, action: PayloadAction<string | undefined>) => {
      state.aggregationIntervalType = action.payload;
    },
    resetAll: () => initialState,
  },
});

export const selectAggregationInterval = (state: RootState) => state.importedMeasurementsOverview.aggregationInterval;

export const selectAggregationIntervalType = (state: RootState) =>
  state.importedMeasurementsOverview.aggregationIntervalType;

export const selectSelectedTso = (state: RootState) => state.importedMeasurementsOverview.selectedTso;

export const selectActiveTsos = (state: RootState) => state.importedMeasurementsOverview.activeTsos;

export const selectActiveTsoName = (state: RootState) => state.importedMeasurementsOverview.activeTsoName;

export const selectStart = (state: RootState) => state.importedMeasurementsOverview.start;

export const selectEnd = (state: RootState) => state.importedMeasurementsOverview.end;

export const selectDeviceTechnicalNames = (state: RootState) => state.importedMeasurementsOverview.deviceTechnicalNames;

export const selectDataToBeDisplayedInChart = (state: RootState) =>
  state.importedMeasurementsOverview.dataToBeDisplayedInChart;

export const selectMeasurementLogSubmissionPayload = createSelector(
  [selectStart, selectEnd, selectDeviceTechnicalNames],
  (start, end, deviceTechnicalNames) => {
    const body = Object.values(deviceTechnicalNames).flat();

    return {
      start,
      end,
      aggregationIntervalId: 1,
      body,
    };
  }
);

export const selectMeasurementLogs = (state: RootState) => state.importedMeasurementsOverview.measurementLogs;

export const {
  setSelectedTso,
  setActiveTsos,
  setActiveTsoName,
  setDeviceTechnicalNames,
  setEnd,
  setStart,
  setMeasurementLogs,
  resetAll,
  setDataToBeDisplayedInChart,
  setAggregationInterval,
  setAggregationIntervalType,
} = importedMeasurementsOverviewSlice.actions;

export default importedMeasurementsOverviewSlice.reducer;
